import { useEffect } from "react";
import { useTheme } from "@/lib/ThemeContext";
import { Link } from "react-router-dom";
import { TERMS, PRIVACY } from "@/data/legal";
import LegalFooter from "@/components/LegalFooter";

const URL_RE = /(https?:\/\/[^\s<]+)/g;

function linkifyHTML(text) {
  // Wrap raw URLs and bare emails — keep simple, no library
  return text
    .split(URL_RE)
    .map((part, i) =>
      URL_RE.test(part) ? (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "var(--tm-accent, #A855F7)", textDecoration: "underline" }}
        >
          {part}
        </a>
      ) : (
        <span key={i}>{part}</span>
      )
    );
}

function Block({ block, t }) {
  if (typeof block === "string") {
    return (
      <p
        className="mb-4"
        style={{ color: "var(--tm-text-primary)", fontSize: "16px", lineHeight: 1.6 }}
      >
        {linkifyHTML(block)}
      </p>
    );
  }
  if (block?.type === "subheading") {
    return (
      <h3
        className="mt-6 mb-2 text-base font-semibold"
        style={{ color: "var(--tm-text-primary)", letterSpacing: "-0.005em" }}
      >
        {block.text}
      </h3>
    );
  }
  if (block?.type === "list") {
    return (
      <ul className="mb-4 pl-6" style={{ listStyle: "disc", color: "var(--tm-text-primary)" }}>
        {block.items.map((item, i) => (
          <li key={i} style={{ marginBottom: "6px", fontSize: "16px", lineHeight: 1.6 }}>
            {linkifyHTML(item)}
          </li>
        ))}
      </ul>
    );
  }
  return null;
}

export default function LegalPage({ doc }) {
  const { tokens: t } = useTheme();
  const data = doc === "privacy" ? PRIVACY : TERMS;

  useEffect(() => {
    document.title = `${data.title} • MMNTM`;
    return () => { document.title = "MMNTM"; };
  }, [data.title]);

  return (
    <div
      data-testid={`legal-page-${doc}`}
      style={{ background: "var(--tm-bg-primary, " + t.bgPrimary + ")", minHeight: "calc(100vh - var(--topbar-height, 0px))" }}
    >
      <article
        className="mx-auto px-6 sm:px-8 py-10 sm:py-12"
        style={{ maxWidth: "800px" }}
      >
        {/* Title */}
        <h1
          className="font-bold mb-2"
          style={{
            color: "var(--tm-text-primary)",
            fontSize: "36px",
            letterSpacing: "-0.02em",
            lineHeight: 1.1,
          }}
          data-testid="legal-title"
        >
          {data.title}
        </h1>

        {/* Metadata */}
        <div
          className="mb-10 pb-6 text-sm"
          style={{
            color: "var(--tm-text-secondary)",
            borderBottom: `1px solid var(--tm-border-color, ${t.borderColor})`,
            lineHeight: 1.7,
          }}
          data-testid="legal-meta"
        >
          <div><strong>Effective Date:</strong> {data.effective}</div>
          <div><strong>Last Updated:</strong> {data.lastUpdated}</div>
          <div><strong>Operator:</strong> {data.operator}</div>
          <div>
            <strong>Contact:</strong>{" "}
            <a
              href={`mailto:${data.contact}`}
              style={{ color: "var(--tm-accent)", textDecoration: "underline" }}
            >
              {data.contact}
            </a>
          </div>
          <div>
            <strong>Website:</strong>{" "}
            <a
              href={data.website}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--tm-accent)", textDecoration: "underline" }}
            >
              {data.website}
            </a>
          </div>
        </div>

        {/* Sections */}
        {data.sections.map((sec, idx) => (
          <section key={idx} className="mb-8" data-testid={`legal-section-${idx + 1}`}>
            <h2
              className="font-bold mb-3"
              style={{
                color: "var(--tm-text-primary)",
                fontSize: "22px",
                letterSpacing: "-0.01em",
                lineHeight: 1.25,
              }}
            >
              {sec.heading}
            </h2>
            {Array.isArray(sec.body)
              ? sec.body.map((b, i) => <Block key={i} block={b} t={t} />)
              : <Block block={sec.body} t={t} />}
          </section>
        ))}

        {/* Cross-link to the other doc */}
        <div
          className="mt-10 pt-6 text-sm"
          style={{
            borderTop: `1px solid var(--tm-border-color, ${t.borderColor})`,
            color: "var(--tm-text-secondary)",
          }}
        >
          {doc === "terms" ? (
            <>See also our <Link to="/privacy" style={{ color: "var(--tm-accent)", textDecoration: "underline" }}>Privacy Policy</Link>.</>
          ) : (
            <>See also our <Link to="/terms" style={{ color: "var(--tm-accent)", textDecoration: "underline" }}>Terms of Service</Link>. Want to leave? <Link to="/delete-account" style={{ color: "var(--tm-accent)", textDecoration: "underline" }} data-testid="legal-delete-account-link">How to delete your account</Link>.</>
          )}
        </div>
      </article>

      <LegalFooter />
    </div>
  );
}
